import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Star, MapPin, Clock, ShieldCheck } from "lucide-react";

const CATEGORY_LABELS = {
  official: "OFFICIAL",
  park_and_ride: "PARK & RIDE",
  meet_and_greet: "MEET & GREET",
  hotel: "HOTEL",
  community: "COMMUNITY",
};
const CATEGORY_COLORS = {
  official: "bg-slate-900 text-white",
  park_and_ride: "bg-sky-50 text-sky-800 border border-sky-200",
  meet_and_greet: "bg-amber-50 text-amber-800 border border-amber-200",
  hotel: "bg-indigo-50 text-indigo-800 border border-indigo-200",
  community: "bg-emerald-50 text-emerald-800 border border-emerald-200",
};

export default function SearchResults() {
  const [params] = useSearchParams();
  const airport = params.get("airport") || "LHR";
  const dropoff = params.get("dropoff");
  const pickup = params.get("pickup");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recommended");
  const [filters, setFilters] = useState({ types: [], maxDistance: null, cancellation: false });

  useEffect(() => {
    setLoading(true);
    api.post("/search", { airport_code: airport, dropoff, pickup })
      .then((r) => setData(r.data))
      .catch(() => setData({ offers: [], airport: null }))
      .finally(() => setLoading(false));
  }, [airport, dropoff, pickup]);

  const days = useMemo(() => {
    try { const d1 = new Date(dropoff), d2 = new Date(pickup); return Math.max(1, Math.ceil((d2 - d1) / 86400000)); } catch { return 1; }
  }, [dropoff, pickup]);

  const offers = useMemo(() => {
    let os = data?.offers || [];
    if (filters.types.length) os = os.filter(o => filters.types.includes(o.category));
    if (filters.maxDistance) os = os.filter(o => o.distance_miles <= filters.maxDistance);
    if (filters.cancellation) os = os.filter(o => o.cancellation_type === "flexible");
    const cmp = {
      cheapest: (a, b) => a.total_price - b.total_price,
      closest: (a, b) => a.distance_miles - b.distance_miles,
      rated: (a, b) => b.rating - a.rating,
      transfer: (a, b) => a.transfer_minutes - b.transfer_minutes,
      recommended: (a, b) => (b.rating * 10 - b.total_price / 5) - (a.rating * 10 - a.total_price / 5),
    }[sort];
    return [...os].sort(cmp);
  }, [data, sort, filters]);

  const toggleType = (t) => setFilters(f => ({ ...f, types: f.types.includes(t) ? f.types.filter(x => x !== t) : [...f.types, t] }));

  const airportName = data?.airport?.name || airport;

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[var(--fpn-navy)]" data-testid="results-title">Parking at {airportName}</h1>
          <div className="text-sm text-slate-600 mt-1" data-testid="results-dates">
            {dropoff && pickup ? `${new Date(dropoff).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} → ${new Date(pickup).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
          </div>
        </div>
        <Link to={`/?airport=${airport}`} className="text-sm font-semibold text-[var(--fpn-navy)] underline" data-testid="modify-search">Modify search</Link>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-8">
        {/* Filters */}
        <aside className="lg:col-span-3">
          <div className="border border-slate-200 rounded-2xl p-5 bg-white space-y-6" data-testid="filters-panel">
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500 mb-3">Sort by</div>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="w-full border border-slate-200 rounded-lg p-2 text-sm" data-testid="sort-select">
                <option value="recommended">Recommended</option>
                <option value="cheapest">Cheapest</option>
                <option value="closest">Closest</option>
                <option value="rated">Highest rated</option>
                <option value="transfer">Shortest transfer</option>
              </select>
            </div>
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500 mb-3">Parking type</div>
              <div className="space-y-2 text-sm">
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <label key={k} className="flex items-center gap-2 cursor-pointer" data-testid={`filter-type-${k}`}>
                    <input type="checkbox" checked={filters.types.includes(k)} onChange={() => toggleType(k)} />
                    {v}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-slate-500 mb-3">Distance</div>
              <select value={filters.maxDistance || ""} onChange={(e) => setFilters(f => ({ ...f, maxDistance: e.target.value ? Number(e.target.value) : null }))} className="w-full border border-slate-200 rounded-lg p-2 text-sm" data-testid="filter-distance">
                <option value="">Any</option>
                <option value="1">Under 1 mile</option>
                <option value="2">Under 2 miles</option>
                <option value="5">Under 5 miles</option>
                <option value="10">Under 10 miles</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm" data-testid="filter-cancellation">
              <input type="checkbox" checked={filters.cancellation} onChange={(e) => setFilters(f => ({ ...f, cancellation: e.target.checked }))} />
              Free cancellation
            </label>
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-9">
          {loading && (
            <div className="space-y-4" data-testid="results-loading">
              {[0, 1, 2].map(i => <div key={i} className="h-40 rounded-2xl border border-slate-200 bg-slate-50 animate-pulse" />)}
            </div>
          )}
          {!loading && offers.length === 0 && (
            <div className="p-10 text-center border border-slate-200 rounded-2xl" data-testid="results-empty">
              <div className="font-display text-xl font-semibold text-[var(--fpn-navy)]">No parking matches your filters</div>
              <p className="text-sm text-slate-500 mt-2">Try widening your distance or clearing parking type filters.</p>
            </div>
          )}
          <div className="space-y-4">
            {!loading && offers.map((o) => (
              <div key={o.offer_id} className="border border-slate-200 rounded-2xl bg-white overflow-hidden md:flex card-lift" data-testid={`result-card-${o.offer_id}`}>
                <div className="md:w-64 h-48 md:h-auto bg-slate-100">
                  <img src={o.image_url} alt={o.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5 md:p-6 flex-1 flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] font-bold px-2 py-1 rounded-md tracking-widest ${CATEGORY_COLORS[o.category] || "bg-slate-100 text-slate-700"}`}>{CATEGORY_LABELS[o.category] || o.category.toUpperCase()}</span>
                      {o.source_type === "community" && (
                        <span className="text-[10px] font-bold px-2 py-1 rounded-md tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                          <ShieldCheck size={10} /> APPROVED SPACE
                        </span>
                      )}
                      {o.verified_host && <span className="text-[10px] font-bold px-2 py-1 rounded-md tracking-widest bg-slate-900 text-white">VERIFIED HOST</span>}
                    </div>
                    <div className="font-display text-xl font-semibold mt-2 text-[var(--fpn-navy)]">{o.name}</div>
                    <div className="mt-2 text-sm text-slate-600 flex items-center flex-wrap gap-4">
                      <span className="inline-flex items-center gap-1"><MapPin size={14} /> {o.distance_miles} mi</span>
                      {o.transfer_minutes ? <span className="inline-flex items-center gap-1"><Clock size={14} /> {o.transfer_minutes} min transfer</span> : <span className="inline-flex items-center gap-1"><Clock size={14} /> On arrival</span>}
                      <span className="inline-flex items-center gap-1"><Star size={14} className="text-amber-500" fill="currentColor" /> {o.rating} ({o.review_count})</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(o.features || []).slice(0, 4).map((f, i) => (
                        <span key={i} className="text-[11px] px-2 py-1 rounded-md bg-slate-50 text-slate-600 border border-slate-100">{f}</span>
                      ))}
                    </div>
                  </div>
                  <div className="md:text-right">
                    <div className="text-xs text-slate-500">Total for {days} day{days > 1 ? "s" : ""}</div>
                    <div className="font-display text-3xl font-bold text-[var(--fpn-navy)]">£{o.total_price.toFixed(2)}</div>
                    <div className="text-xs text-slate-500 mb-3">£{(o.total_price / days).toFixed(2)}/day</div>
                    <Link
                      to={`/parking/${encodeURIComponent(o.offer_id)}?airport=${airport}&dropoff=${encodeURIComponent(dropoff || "")}&pickup=${encodeURIComponent(pickup || "")}`}
                      data-testid={`view-parking-${o.offer_id}`}
                      className="btn-amber inline-block rounded-full px-5 py-2.5 text-sm"
                    >View Parking</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
